import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import './LeadDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtTime = (d) =>
  d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—';

const todayISO = () => new Date().toISOString().slice(0, 10);

const shiftDate = (iso, days) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const fmtDisplayDate = (iso) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

const STATUS_ORDER = ['New', 'Contacted', 'Interested', 'Converted', 'Subscribed', 'Lost Subscription', 'Lost'];
const STATUS_COLOR = {
  New: '#3498db', Contacted: '#f39c12', Interested: '#8e44ad',
  Converted: '#27ae60', Subscribed: '#16a085', 'Lost Subscription': '#d35400', Lost: '#e74c3c',
};
const STATUS_BADGE = {
  New: 'status-new', Contacted: 'status-contacted', Interested: 'status-interested',
  Converted: 'status-converted', Lost: 'status-lost',
};

// ── Drill-down modal ──────────────────────────────────────────

function LeadsTable({ rows }) {
  return rows.length === 0
    ? <p className="ld-empty">No records found.</p>
    : (
      <table className="ld-table">
        <thead>
          <tr>
            <th>Name</th><th>Phone</th><th>Status</th>
            <th>Channel</th><th>Assigned To</th><th>Created</th><th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td><strong>{r.name}</strong></td>
              <td>{r.phone}</td>
              <td>
                <span className={`state-badge ${STATUS_BADGE[r.status] || ''}`}>{r.status}</span>
              </td>
              <td>{r.source_channel || '—'}</td>
              <td>{r.assigned_to_name || '—'}</td>
              <td>{fmtDate(r.created_date)}</td>
              <td>{fmtDate(r.updated_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
}

function OrdersTable({ rows }) {
  return rows.length === 0
    ? <p className="ld-empty">No orders found.</p>
    : (
      <table className="ld-table">
        <thead>
          <tr>
            <th>Order No</th><th>Customer</th><th>Lead Name</th><th>Phone</th>
            <th>Order Date</th><th>Delivery Date</th><th>State</th><th>Assigned To</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td><strong>{r.order_no}</strong></td>
              <td>{r.customer_name}</td>
              <td>{r.lead_name}</td>
              <td>{r.phone}</td>
              <td>{fmtDate(r.order_date)}</td>
              <td>{fmtDate(r.delivery_date)}</td>
              <td><span className="ld-order-state">{r.order_lc_state || r.order_state}</span></td>
              <td>{r.assigned_to_name || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
}

function DrillDownModal({ modal, onClose }) {
  if (!modal.open) return null;
  return (
    <div className="ld-modal-overlay" onClick={onClose}>
      <div className="ld-modal" onClick={e => e.stopPropagation()}>
        <div className="ld-modal-header">
          <h3 className="ld-modal-title">{modal.title}</h3>
          <button className="ld-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="ld-modal-body">
          {modal.loading && <div className="ld-loading">Loading…</div>}
          {modal.error  && <div className="ld-error">{modal.error}</div>}
          {!modal.loading && !modal.error && (
            <div className="ld-table-wrap">
              {modal.kind === 'orders'
                ? <OrdersTable rows={modal.rows} />
                : <LeadsTable  rows={modal.rows} />}
            </div>
          )}
        </div>
        <div className="ld-modal-footer">
          {!modal.loading && !modal.error && (
            <span className="ld-modal-count">{modal.rows.length} record{modal.rows.length !== 1 ? 's' : ''}</span>
          )}
          <button className="ld-modal-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Assignee Interactions Modal ───────────────────────────────

function AssigneeInteractionsModal({ assigneeId, assigneeName, onClose }) {
  const [date, setDate] = useState(todayISO);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const abortRef = useRef(null);

  useEffect(() => {
    if (!assigneeId) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true); setError(''); setRows([]);
    fetch(`${API_URL}/leads/dashboard/assignee-interactions?user_id=${assigneeId}&date=${date}`,
      { headers: authHeader(), signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error('Failed to load'); return r.json(); })
      .then(data => { setRows(data); setLoading(false); })
      .catch(err => { if (err.name !== 'AbortError') { setError(err.message); setLoading(false); } });
    return () => ctrl.abort();
  }, [assigneeId, date]);

  if (!assigneeId) return null;

  return (
    <div className="ld-modal-overlay" onClick={onClose}>
      <div className="ld-modal ld-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="ld-modal-header">
          <h3 className="ld-modal-title">Interactions — {assigneeName}</h3>
          <button className="ld-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="ld-ai-date-nav">
          <button className="ld-ai-nav-btn" onClick={() => setDate(d => shiftDate(d, -1))}>‹</button>
          <span className="ld-ai-date-label">{fmtDisplayDate(date)}</span>
          <button className="ld-ai-nav-btn" onClick={() => setDate(d => shiftDate(d, 1))}
            disabled={date >= todayISO()}>›</button>
        </div>

        <div className="ld-modal-body">
          {loading && <div className="ld-loading">Loading…</div>}
          {error   && <div className="ld-error">{error}</div>}
          {!loading && !error && rows.length === 0 && (
            <p className="ld-empty">No interactions logged on this date.</p>
          )}
          {!loading && !error && rows.length > 0 && (
            <div className="ld-table-wrap">
              <table className="ld-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Type</th>
                    <th>Channel</th>
                    <th>Direction</th>
                    <th>Outcome</th>
                    <th>Summary</th>
                    <th>Next Action</th>
                    <th>Due</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtTime(r.interaction_date)}</td>
                      <td><strong>{r.entity_name || '—'}</strong></td>
                      <td>{r.entity_phone || '—'}</td>
                      <td>{r.interaction_type}</td>
                      <td>{r.channel || '—'}</td>
                      <td>{r.direction || '—'}</td>
                      <td>{r.outcome || '—'}</td>
                      <td style={{ maxWidth: '220px' }}>{r.summary || '—'}</td>
                      <td>{r.next_action || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{r.next_action_date ? fmtDate(r.next_action_date) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="ld-modal-footer">
          {!loading && !error && (
            <span className="ld-modal-count">{rows.length} interaction{rows.length !== 1 ? 's' : ''}</span>
          )}
          <button className="ld-modal-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── CEO sub-components ────────────────────────────────────────

function KpiCard({ label, value, sub, color, onClick, subonClick }) {
  return (
    <div
      className={`ld-kpi-card ${onClick ? 'ld-kpi-card--clickable' : ''}`}
      style={{ borderTopColor: color }}
      onClick={onClick}
      title={onClick ? 'Click to view details' : undefined}
    >
      <div className="ld-kpi-value" style={{ color }}>{value ?? '—'}</div>
      <div className="ld-kpi-label">{label}</div>
      {sub && (
        <div
          className={`ld-kpi-sub ${subonClick ? 'ld-kpi-sub--link' : ''}`}
          onClick={subonClick ? (e) => { e.stopPropagation(); subonClick(); } : undefined}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

function Funnel({ funnel, ordersTotal, onDrillDown }) {
  const ordered = STATUS_ORDER.map(s => funnel.find(r => r.status === s)).filter(Boolean);
  const allRows = [
    ...ordered,
    { status: 'Orders Placed', count: ordersTotal ?? 0 },
  ];
  const max = Math.max(...allRows.map(r => Number(r.count)), 1);
  const COLOR_ORDERS = '#1abc9c';

  const typeFor = (status) =>
    status === 'Orders Placed' ? 'orders_total' : `status_${status}`;

  return (
    <div className="ld-section">
      <h3 className="ld-section-title">Pipeline Funnel</h3>
      <div className="ld-funnel">
        {allRows.map(r => {
          const color = r.status === 'Orders Placed' ? COLOR_ORDERS : (STATUS_COLOR[r.status] || '#aaa');
          return (
            <div key={r.status} className="ld-funnel-row">
              <div className="ld-funnel-label">{r.status}</div>
              <div className="ld-funnel-bar-wrap">
                <div
                  className="ld-funnel-bar"
                  style={{ width: `${(Number(r.count) / max) * 100}%`, background: color }}
                />
              </div>
              <div
                className="ld-funnel-count ld-funnel-count--link"
                style={{ color }}
                onClick={() => onDrillDown(typeFor(r.status), `${r.status} Leads`)}
                title="Click to view details"
              >
                {r.count}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SourceBreakdown({ rows }) {
  const max = Math.max(...rows.map(r => Number(r.total)), 1);
  return (
    <div className="ld-section">
      <h3 className="ld-section-title">Leads by Channel</h3>
      <div className="ld-source-list">
        {rows.map(r => {
          const convRate = r.total > 0 ? ((r.converted / r.total) * 100).toFixed(0) : 0;
          return (
            <div key={r.source_channel} className="ld-source-row">
              <div className="ld-source-name">{r.source_channel}</div>
              <div className="ld-source-bar-wrap">
                <div className="ld-source-bar" style={{ width: `${(Number(r.total) / max) * 100}%` }} />
                <div className="ld-source-bar ld-source-bar--converted" style={{ width: `${(Number(r.converted) / max) * 100}%` }} />
              </div>
              <div className="ld-source-stats">
                <span>{r.total} leads</span>
                <span className="ld-source-conv">{r.converted} conv ({convRate}%)</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="ld-legend">
        <span className="ld-legend-dot" style={{ background: '#b0bec5' }} /> Total
        <span className="ld-legend-dot" style={{ background: '#27ae60', marginLeft: '1rem' }} /> Converted
      </div>
    </div>
  );
}

function WeeklyTrend({ rows }) {
  const max = Math.max(...rows.flatMap(r => [
    Number(r.leads_added || 0), Number(r.converted || 0), Number(r.orders_placed || 0),
  ]), 1);
  return (
    <div className="ld-section">
      <h3 className="ld-section-title">Weekly Trend (last 8 weeks)</h3>
      {rows.length === 0 ? (
        <p className="ld-empty">No data in the last 8 weeks.</p>
      ) : (
        <div className="ld-trend">
          {rows.map((r, i) => (
            <div key={i} className="ld-trend-col">
              <div className="ld-trend-bars">
                <div className="ld-trend-bar ld-trend-bar--added"
                  style={{ height: `${(Number(r.leads_added || 0) / max) * 80}px` }}
                  title={`Added: ${r.leads_added}`} />
                <div className="ld-trend-bar ld-trend-bar--conv"
                  style={{ height: `${(Number(r.converted || 0) / max) * 80}px` }}
                  title={`Converted: ${r.converted}`} />
                <div className="ld-trend-bar ld-trend-bar--orders"
                  style={{ height: `${(Number(r.orders_placed || 0) / max) * 80}px` }}
                  title={`Orders: ${r.orders_placed || 0}`} />
              </div>
              <div className="ld-trend-label">{r.week_label}</div>
            </div>
          ))}
          <div className="ld-legend" style={{ marginTop: '0.5rem' }}>
            <span className="ld-legend-dot" style={{ background: '#3498db' }} /> Added
            <span className="ld-legend-dot" style={{ background: '#27ae60', marginLeft: '1rem' }} /> Converted
            <span className="ld-legend-dot" style={{ background: '#1abc9c', marginLeft: '1rem' }} /> Orders Placed
          </div>
        </div>
      )}
    </div>
  );
}

function Leaderboard({ rows, onAssigneeClick }) {
  return (
    <div className="ld-section ld-section--wide">
      <h3 className="ld-section-title">Assignee Performance — Last 30 Days</h3>
      <div className="ld-table-wrap">
        <table className="ld-table">
          <thead>
            <tr>
              <th>Assignee</th>
              <th>Active Leads</th>
              <th>Converted (30d)</th>
              <th>Subscribed</th>
              <th>Orders (30d)</th>
              <th>Lost (30d)</th>
              <th>Lost Sub (30d)</th>
              <th>Conv. Rate</th>
              <th>Avg First Response</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>
                  {r.assignee_id
                    ? <button className="ld-assignee-link" onClick={() => onAssigneeClick(r.assignee_id, r.assignee_name)}>{r.assignee_name}</button>
                    : <strong>{r.assignee_name}</strong>}
                </td>
                <td>{r.active}</td>
                <td><span className="ld-badge ld-badge--green">{r.converted_mtd}</span></td>
                <td>
                  {Number(r.subscribed) > 0
                    ? <span className="ld-badge ld-badge--teal">{r.subscribed}</span>
                    : <span className="ld-muted">0</span>}
                </td>
                <td>
                  {Number(r.orders_30d) > 0
                    ? <span className="ld-badge ld-badge--teal">{r.orders_30d}</span>
                    : <span className="ld-muted">0</span>}
                </td>
                <td>
                  {Number(r.lost_mtd) > 0
                    ? <span className="ld-badge ld-badge--red">{r.lost_mtd}</span>
                    : <span className="ld-muted">0</span>}
                </td>
                <td>
                  {Number(r.lost_subscription_mtd) > 0
                    ? <span className="ld-badge ld-badge--orange">{r.lost_subscription_mtd}</span>
                    : <span className="ld-muted">0</span>}
                </td>
                <td>{r.conv_rate != null ? `${r.conv_rate}%` : '—'}</td>
                <td>{r.avg_response_days != null ? `${r.avg_response_days} days` : '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="ld-empty">No data.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Assignee sub-components ───────────────────────────────────

function AttentionCard({ count, label, color, icon }) {
  return (
    <div className="ld-attention-card" style={{ borderLeftColor: color }}>
      <div className="ld-attention-icon">{icon}</div>
      <div>
        <div className="ld-attention-count" style={{ color }}>{count}</div>
        <div className="ld-attention-label">{label}</div>
      </div>
    </div>
  );
}

function MyPipeline({ rows }) {
  const order = ['New', 'Contacted', 'Interested'];
  const ordered = order.map(s => rows.find(r => r.status === s) || { status: s, count: 0 });
  const total = ordered.reduce((s, r) => s + Number(r.count), 0);
  return (
    <div className="ld-section">
      <h3 className="ld-section-title">My Pipeline</h3>
      <div className="ld-my-pipeline">
        {ordered.map(r => (
          <div key={r.status} className="ld-pipeline-step">
            <div className="ld-pipeline-count" style={{ color: STATUS_COLOR[r.status] }}>{r.count}</div>
            <div className="ld-pipeline-dot" style={{ background: STATUS_COLOR[r.status] }} />
            <div className="ld-pipeline-label">{r.status}</div>
          </div>
        ))}
        <div className="ld-pipeline-step">
          <div className="ld-pipeline-count" style={{ color: '#555' }}>{total}</div>
          <div className="ld-pipeline-dot" style={{ background: '#ccc' }} />
          <div className="ld-pipeline-label">Total Active</div>
        </div>
      </div>
    </div>
  );
}

function AttentionList({ rows }) {
  const navigate = useNavigate();
  return (
    <div className="ld-section ld-section--wide">
      <h3 className="ld-section-title">Needs Attention</h3>
      {rows.length === 0 ? (
        <p className="ld-empty">All caught up! No overdue or untouched leads.</p>
      ) : (
        <div className="ld-table-wrap">
          <table className="ld-table">
            <thead>
              <tr>
                <th>Name</th><th>Phone</th><th>Status</th>
                <th>Interactions</th><th>Next Action</th><th>Due</th><th>Added</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const isOverdue = r.next_action_date && new Date(r.next_action_date) < new Date();
                return (
                  <tr key={r.id} className={isOverdue ? 'ld-row--overdue' : ''}
                    style={{ cursor: 'pointer' }} onClick={() => navigate('/leads')}
                    title="Go to Leads to view details">
                    <td><strong>{r.name}</strong></td>
                    <td>{r.phone}</td>
                    <td>
                      <span className="ld-status-dot" style={{ background: STATUS_COLOR[r.status] }} />
                      {r.status}
                    </td>
                    <td style={{ textAlign: 'center' }}>{r.interaction_count}</td>
                    <td>{r.next_action || <span className="ld-muted">—</span>}</td>
                    <td>
                      {r.next_action_date
                        ? <span className={isOverdue ? 'ld-overdue-text' : ''}>{fmtDate(r.next_action_date)}</span>
                        : <span className="ld-muted">—</span>}
                    </td>
                    <td>{fmtDate(r.created_date)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ConversionsList({ rows }) {
  return (
    <div className="ld-section">
      <h3 className="ld-section-title">My Conversions — Last 30 Days</h3>
      {rows.length === 0 ? (
        <p className="ld-empty">No conversions in the last 30 days.</p>
      ) : (
        <div className="ld-conv-list">
          {rows.map((r, i) => (
            <div key={i} className="ld-conv-item">
              <div className="ld-conv-check">✓</div>
              <div>
                <div className="ld-conv-name">{r.name}</div>
                <div className="ld-conv-date">{r.phone} · converted {fmtDate(r.updated_date)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────

const BLANK_MODAL = { open: false, title: '', type: null, kind: null, rows: [], loading: false, error: '' };
const BLANK_AI_MODAL = { assigneeId: null, assigneeName: '' };

const LeadDashboard = () => {
  const { user } = useContext(AuthContext);
  const [data,  setData]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,  setError]  = useState('');
  const [modal,  setModal]  = useState(BLANK_MODAL);
  const [aiModal, setAiModal] = useState(BLANK_AI_MODAL);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/leads/dashboard`, { headers: authHeader() });
        if (!res.ok) throw new Error('Failed to load dashboard');
        setData(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openDrillDown = useCallback(async (type, title) => {
    setModal({ open: true, title, type, kind: null, rows: [], loading: true, error: '' });
    try {
      const res = await fetch(`${API_URL}/leads/dashboard/detail?type=${type}`, { headers: authHeader() });
      if (!res.ok) throw new Error('Failed to load details');
      const json = await res.json();
      setModal(m => ({ ...m, kind: json.kind, rows: json.rows, loading: false }));
    } catch (err) {
      setModal(m => ({ ...m, error: err.message, loading: false }));
    }
  }, []);

  const closeModal = useCallback(() => setModal(BLANK_MODAL), []);

  const openAiModal = useCallback((assigneeId, assigneeName) =>
    setAiModal({ assigneeId, assigneeName }), []);
  const closeAiModal = useCallback(() => setAiModal(BLANK_AI_MODAL), []);

  if (loading) return <div className="ld-loading">Loading dashboard…</div>;
  if (error)   return <div className="ld-error">{error}</div>;
  if (!data)   return null;

  return (
    <div className="ld-page">
      <div className="ld-header">
        <div>
          <h2 className="ld-title">Lead Dashboard</h2>
          <p className="ld-subtitle">
            {data.is_ceo ? 'Organisation-wide overview' : `Welcome, ${user?.name || 'there'} — your pipeline at a glance`}
          </p>
        </div>
        <Link to="/leads" className="ld-back-btn">← Back to Leads</Link>
      </div>

      {data.is_ceo ? (
        <>
          <div className="ld-kpi-row">
            <KpiCard label="Active Leads"       value={data.kpi.active}          color="#3498db"
              onClick={() => openDrillDown('active', 'Active Leads')} />
            <KpiCard label="New (Last 30 Days)" value={data.kpi.new_mtd}         color="#8e44ad"
              onClick={() => openDrillDown('new_30d', 'New Leads — Last 30 Days')} />
            <KpiCard
              label="Converted (30d)" value={data.kpi.converted_mtd}   color="#27ae60"
              sub={data.kpi.new_mtd > 0 ? `${((data.kpi.converted_mtd / data.kpi.new_mtd) * 100).toFixed(0)}% of new` : null}
              onClick={() => openDrillDown('converted_30d', 'Converted Leads — Last 30 Days')} />
            <KpiCard label="Subscribed"         value={data.kpi.subscribed}      color="#16a085"
              onClick={() => openDrillDown('subscribed', 'Subscribed Leads')} />
            <KpiCard
              label="Orders Placed (30d)" value={data.kpi.orders_30d}  color="#1abc9c"
              sub={`${data.kpi.orders_total} all-time`}
              onClick={() => openDrillDown('orders_30d', 'Orders Placed — Last 30 Days')}
              subonClick={() => openDrillDown('orders_total', 'Orders Placed — All Time')} />
            <KpiCard label="Lost (30d)"         value={data.kpi.lost_mtd}        color="#e74c3c"
              onClick={() => openDrillDown('lost_30d', 'Lost Leads — Last 30 Days')} />
            <KpiCard label="Lost Sub (30d)"     value={data.kpi.lost_subscription_mtd} color="#d35400"
              onClick={() => openDrillDown('lost_subscription_30d', 'Lost Subscription — Last 30 Days')} />
            <KpiCard
              label="Avg Days to Convert"
              value={data.kpi.avg_days_to_convert != null ? `${data.kpi.avg_days_to_convert}d` : '—'}
              color="#f39c12" sub="converted in last 30 days" />
          </div>

          <div className="ld-row">
            <Funnel funnel={data.funnel} ordersTotal={data.kpi.orders_total} onDrillDown={openDrillDown} />
            <SourceBreakdown rows={data.source_breakdown} />
          </div>

          <WeeklyTrend rows={data.weekly_trend} />
          <Leaderboard rows={data.assignee_leaderboard} onAssigneeClick={openAiModal} />
        </>
      ) : (
        <>
          <div className="ld-attention-row">
            <AttentionCard count={data.attention.overdue}               label="Overdue Follow-ups"   color="#e74c3c" icon="⚠️" />
            <AttentionCard count={data.attention.due_today}             label="Due Today"             color="#f39c12" icon="📅" />
            <AttentionCard count={data.attention.no_interaction}        label="Never Contacted"       color="#8e44ad" icon="💤" />
            <AttentionCard count={data.attention.active}                label="Total Active"          color="#3498db" icon="📋" />
            <AttentionCard count={data.attention.converted_mtd}         label="Converted (30d)"       color="#27ae60" icon="🎉" />
            <AttentionCard count={data.attention.subscribed}            label="Subscribed"            color="#16a085" icon="✅" />
            <AttentionCard count={data.attention.lost_subscription_mtd} label="Lost Sub (30d)"        color="#d35400" icon="📉" />
          </div>

          <div className="ld-row">
            <MyPipeline rows={data.my_pipeline} />
            <ConversionsList rows={data.my_conversions_mtd} />
          </div>

          <AttentionList rows={data.attention_list} />
        </>
      )}

      <DrillDownModal modal={modal} onClose={closeModal} />
      <AssigneeInteractionsModal
        assigneeId={aiModal.assigneeId}
        assigneeName={aiModal.assigneeName}
        onClose={closeAiModal}
      />
    </div>
  );
};

export default LeadDashboard;
